/**
 * SessionDetailWithRegister — composition-root wiring for the 11E.7
 * session page.
 *
 * Three features meet here and none of them may import each other:
 *   - `features/calendar` owns the session page chrome and actions
 *   - `features/attendance` owns the roster read and the marking write
 *   - `features/bookings` owns enrolment (it is the sole producer of
 *     `session.student_ids`)
 *   - `features/people` owns student search
 *
 * Routes sits above the feature layer, so the wiring lives here — same
 * pattern as `bookingPickers.tsx`.
 *
 * Why enrolling creates a booking
 * -------------------------------
 * `create_booking` writes the booking document and `ArrayUnion`s the
 * student onto `session.student_ids` in one Firestore batch; cancelling
 * `ArrayRemove`s them. Phase 5 attendance reads that same array. Enrolling
 * by any other write path would fork the roster into two sources of truth,
 * which the 11E.7 cross-reflection invariant forbids.
 */

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { Dropdown } from '@/components/common';
import { SessionDetailPage, type SessionResponse } from '@/features/calendar';
import { SessionRegister } from '@features/attendance/pages/SessionRegister';
import {
  cancelBooking,
  createBooking,
  listBookings,
} from '@features/bookings/api/bookings';
import { useStudent, useStudentsList } from '@features/people/api/students';

/** Searchable student picker used to enrol into the session. */
const StudentPicker: React.FC<{ onPick: (studentId: string) => void }> = ({ onPick }) => {
  const [search, setSearch] = useState('');
  const { data } = useStudentsList({ search, status: 'active', page_size: 20 });

  const options = useMemo(
    () => [
      { value: '', label: 'Search a student to enrol…' },
      ...(data?.items ?? []).map((s) => ({
        value: s.id,
        label: `${s.first_name} ${s.last_name}`,
      })),
    ],
    [data],
  );

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <input
        type="search"
        placeholder="Search students…"
        aria-label="Search students to enrol"
        data-testid="candeza-register-student-search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          padding: '6px 10px',
          border: '1px solid #cbd5e1',
          borderRadius: 6,
          fontSize: 13,
          minWidth: 200,
        }}
      />
      <Dropdown
        options={options}
        value=""
        onChange={(v) => {
          if (v) onPick(v);
        }}
        testId="candeza-register-student-select"
      />
    </div>
  );
};

/**
 * Session-page rail "Grades / Add grades" link (11E.8, D5) — deep-links
 * into the 10B instructor-report form pre-filled with student + period.
 * A session with exactly one enrolled student pre-fills that student by
 * name (fetched here so the ReportForm's search-select input isn't blank
 * despite `student_id` being set); zero or multiple students land on the
 * form with just the period pre-filled and the teacher picks the student
 * via the search-select built for this same prompt.
 */
const GradesLink: React.FC<{ session: SessionResponse }> = ({ session }) => {
  const soleStudentId = session.student_ids?.length === 1 ? session.student_ids[0] : undefined;
  const { data: soleStudent } = useStudent(soleStudentId ?? '');

  const params = new URLSearchParams();
  if (soleStudentId) {
    params.set('student', soleStudentId);
    if (soleStudent) {
      params.set('studentName', `${soleStudent.first_name} ${soleStudent.last_name}`.trim());
    }
  }
  params.set('period_start', session.starts_at.slice(0, 10));
  params.set('period_end', session.ends_at.slice(0, 10));

  return (
    <Link to={`/reports?${params.toString()}`} data-testid="candeza-session-grades-link">
      Add grades
    </Link>
  );
};

export const SessionDetailWithRegister: React.FC = () => {
  const queryClient = useQueryClient();

  /**
   * `createBooking`/`cancelBooking` here are the bare API functions, not
   * the `useCreateBooking`/`useCancelBooking` MUTATION HOOKS — those hooks'
   * `onSuccess` already invalidates `['calendar','sessions']` (fixed
   * earlier in 11E.7 for the calendar chip's booked count), but that fix
   * lives on hooks this composition doesn't use. Without this, the
   * session page's own capacity readout and the calendar chip both went
   * stale after an enrol/unenrol until the 15s `staleTime` lapsed — caught
   * by the session-register E2E spec, not by any unit test, because unit
   * tests mock the query client and never observe real staleness.
   */
  const invalidateCalendarSessions = () => {
    void queryClient.invalidateQueries({ queryKey: ['calendar', 'sessions'] });
  };

  const renderRegister = (session: SessionResponse) => {
    const enroll = async (studentId: string) => {
      await createBooking({
        student_id: studentId,
        session_id: session.id,
        subject_id: session.subject_id,
        // `regular` is the standard subscription lesson and consumes an
        // entitlement, matching what enrolling into a scheduled class
        // means commercially. A student with no eligible entitlement gets
        // a 422 the caller surfaces — deliberately NOT auto-forced, since
        // silently overriding would hand out free lessons.
        booking_type: 'regular',
        starts_at: session.starts_at,
        ends_at: session.ends_at,
        source: 'admin',
        // No silent entitlement override — a 422 surfaces to the operator.
        force: false,
      });
      invalidateCalendarSessions();
    };

    const unenroll = async (studentId: string) => {
      // Booking ids are deterministic but re-bookings get a `__rN` suffix,
      // so resolve by query rather than reconstructing the id.
      const existing = await listBookings({ sessionId: session.id, limit: 200 });
      const match = existing.items.find(
        (b) => b.student_id === studentId && b.status !== 'cancelled',
      );
      if (!match) throw new Error('No active booking found for this student.');
      await cancelBooking(match.id, { reason: 'Unenrolled from session register' });
      invalidateCalendarSessions();
    };

    return (
      <SessionRegister
        sessionId={session.id}
        readOnly={session.status === 'cancelled'}
        onEnroll={enroll}
        onUnenroll={unenroll}
        renderStudentPicker={(onPick) => <StudentPicker onPick={onPick} />}
      />
    );
  };

  return (
    <SessionDetailPage
      renderRegister={renderRegister}
      renderGradesLink={(session) => <GradesLink session={session} />}
    />
  );
};
